import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { PrimaryButton } from "@components/Buttons";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import queryKeys from "@configs/react-query/query-key";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const ProjectDetailsModal = ({
  isOpen,
  onClose,
  projectSeq,
  "data-testid": dataTestId = "project-details-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [licensePolicy, setLicensePolicy] = useState(null);

  const getProjectData = useFetchData(
    [queryKeys.getProjectDetail, projectSeq],
    queryKeys.getProjectDetail + "/" + projectSeq,
    {
      method: "GET",
    },
    {},
    {
      enabled: !!projectSeq,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const getLicensePolicy = useMutationData(
    (lisencePolicySeq) => `${queryKeys.getLicensePolicyByLicenseId}/${lisencePolicySeq}`,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (response && typeof response === "object") {
          setLicensePolicy(response);
        }
      },
      onError: (error) => {
        console.error("Error fetching license policy:", error);
      },
    },
  );

  useEffect(() => {
    if (getProjectData.data && getProjectData.data.licensePolicySeq) {
      getLicensePolicy.mutate(getProjectData.data.licensePolicySeq);
    } else {
      setLicensePolicy(null);
    }
  }, [getProjectData.data?.licensePolicySeq]);

  if (!isOpen || !projectSeq) return null;

  const projectData = getProjectData.data || {};

  const details = [
    { key: "project-id", label: t("project.project-id"), value: projectData.projectId || "---" },
    { key: "project-name", label: t("project.project-name"), value: projectData.projectName || "---" },
    { key: "completion-request-date", label: t("project.completion-request-date"), value: projectData.completionRequestDt ? formatDate(projectData.completionRequestDt) : "---" },
    { key: "asset-classification", label: t("project.asset-classification"), value: projectData.assetClassificationName || "---" },
    { key: "license-class", label: t("project.license-class"), value: projectData.licenseClassCdName || "---" },
    { key: "license-identifier", label: t("project.license-identifier"), value: projectData.licenseIdentifier || "---" },
    { key: "license", label: t("project.license"), value: projectData.licenseName || "---" },
    { key: "license-policy", label: t("project.license-policy"), value: licensePolicy ? licensePolicy.policyName : "---" },
    { key: "creator", label: t("common.creator"), value: <UserNameWithId name={projectData.createrName} id={projectData.createrId} layout="inline" /> },
    { key: "created-at", label: t("common.create-at"), value: projectData.createdAt ? formatDateTime(projectData.createdAt) : "---" },
  ];

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-center backdrop-blur-[1px]`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`${colors.bgModal} w-[90vw] max-w-3xl max-h-[90vh] flex-shrink-0 overflow-auto rounded-xl shadow-xl`}
        data-testid={dataTestId}
      >
        {/* Header */}
        <div className={`px-6 pt-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
              {t("project.project-information")}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="p-6" data-testid={`${dataTestId}-content`}>
          {getProjectData.isLoading ? (
            <div className="flex items-center justify-center py-8" data-testid={fieldValueTestId("loading")}>
              <div className="flex items-center space-x-2">
                <div className={`h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                <span className={`${colors.textSecondary} text-sm`}>{t("common.loading")}</span>
              </div>
            </div>
          ) : (
            <>
              <div className={`rounded-xl border ${colors.borderPrimary} overflow-hidden`}>
                <div className="space-y-0">
                  {details.map((detail, index) => (
                    <div
                      key={detail.key}
                      className={`flex items-center py-3 ${index % 2 === 0 ? colors.bgInput : ""}`}
                      data-testid={fieldContainerTestId(detail.key)}
                    >
                      <div className={`w-1/3 text-sm font-medium ${colors.textSubTitle} px-4`} data-testid={fieldLabelTestId(detail.key)}>
                        {detail.label}:
                      </div>
                      <div className="w-2/3 text-sm px-4" data-testid={fieldValueTestId(detail.key)}>
                        <span className={colors.textPrimary}>{detail.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {projectData.description && (
                <div className={`mt-4 pt-4 border-t ${colors.borderPrimary}`} data-testid={fieldContainerTestId("description")}>
                  <div className={`rounded-lg border ${colors.borderPrimary} overflow-hidden`}>
                    <div className={`flex items-start py-3 ${colors.bgSecondary} px-4`}>
                      <div className={`w-1/3 text-sm font-medium ${colors.textSubTitle}`} data-testid={fieldLabelTestId("description")}>
                        {t("project.description")}:
                      </div>
                      <div className="w-2/3 text-sm" data-testid={fieldValueTestId("description")}>
                        <span className={colors.textPrimary}>{projectData.description}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end space-x-3">
            <PrimaryButton data-testid={`${dataTestId}-close-button`} onClick={onClose}>
              {t("common.close-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsModal;
